import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  SegmentOperator as SegmentOperatorEnum,
  ConditionTypeEnum,
  CountOperator,
  TimeBoundaryType,
  type TimeBoundary,
  type CreateSegmentInput,
  type CanonicalEventName,
  ConditionGroupInput,
  type SegmentDefinitionInput
} from '@/gql/graphql';
import { FetchResult } from '@apollo/client';
import type { MutationFunctionOptions, LazyQueryExecFunction } from '@apollo/client';

// --- Constants ---
const DEFAULT_TIME_BOUNDARY: TimeBoundary = { type: TimeBoundaryType.AnyTime };

/**
 * Zustand store for managing the state of the segment builder UI.
 *
 * This store holds the temporary state of a segment definition (name, description, criteria)
 * as it's being edited by the user. It tracks changes (dirty state), handles saving
 * the segment via a provided mutation function, and manages the state for fetching
 * audience estimates via a provided query function.
 */

// --- State Interfaces ---

/** Represents a single Event condition within the builder's state. */
export interface ConditionState {
  /** Internal identifier for the condition within the store. */
  clientId: string;
  /** Specifies the type of condition (currently only EVENT). */
  conditionType: ConditionTypeEnum.Event;
  /** The canonical name of the event being tracked. */
  eventName: CanonicalEventName;
  /** The comparison operator for the event count (e.g., GREATER_THAN). */
  operator: CountOperator;
  /** The target number of times the event should occur. */
  numTimes: number;
  /** The time window within which the event condition applies. Null means 'Any Time'. */
  timeBoundary: TimeBoundary | null;
  // TODO: Add eventPropertyFilters?: EventPropertyFilterInput[]
}

/** Represents a group of conditions within the builder's state. */
export interface ConditionGroupState {
  /** The database ID of the group, if it exists. */
  id?: string;
  /** Internal identifier for the group within the store. */
  clientId: string;
  /** The logical operator (AND/OR) combining conditions within this group. */
  operator: SegmentOperatorEnum;
  /** An array of conditions belonging to this group. */
  conditions: ConditionState[];
}

/** Input structure for adding a new condition to a group via the store action. */
// Note: clientId and conditionType are handled internally by the action.
// Operator, numTimes, and timeBoundary have defaults if not provided.
type AddConditionInput = Omit<ConditionState, 'clientId' | 'conditionType'> & {
  operator?: CountOperator;
  numTimes?: number;
  timeBoundary?: TimeBoundary | null;
};

/** Input structure for partially updating an existing condition in a group. */
type UpdateConditionInput = Partial<Omit<ConditionState, 'clientId' | 'conditionType'>>;

// --- Store State Interface ---

/** Defines the complete state structure managed by the segment builder store. */
interface SegmentBuilderState {
  /** The name of the segment being built/edited. */
  name: string;
  /** The description of the segment. */
  description: string;
  /** The root operator (AND/OR) combining the condition groups. */
  rootOperator: SegmentOperatorEnum;
  /** An array of condition groups defining the segment criteria. */
  groups: ConditionGroupState[];

  /** Flag indicating if the store has been initialized with segment data. */
  isInitialized: boolean;
  /** Flag indicating if the current state differs from the initial state after initialization/saving. */
  isDirty: boolean;
  /**
   * A snapshot of the store's state taken upon initialization or after a successful save.
   * Used for comparison to determine the `isDirty` status.
   */
  initialState: {
    name: string;
    description: string;
    rootOperator: SegmentOperatorEnum;
    groups: ConditionGroupState[];
  } | null;

  /** Flag indicating if a save operation is currently in progress. */
  isSaving: boolean;
  /** Error message if the last save operation failed, otherwise null. */
  saveError: string | null;
  /** Flag indicating if initial segment data is being fetched (used by the hook, potentially). */
  isFetchingInitial: boolean;
  /** Error message if fetching initial data failed (used by the hook, potentially). */
  fetchInitialError: string | null;
  /** The estimated audience count for the current segment definition. */
  estimateCount: number | null;
  /** Flag indicating if an estimation query is currently in progress. */
  isEstimating: boolean;
  /** Error message if the last estimation query failed, otherwise null. */
  estimateError: string | null;
}

// Define the type for the mutation function passed from the component
// Use FetchResult for the promise return type
type UpdateSegmentMutationFn = (
  options: MutationFunctionOptions<
    { updateSegment: string },
    { segment_id: string; input: CreateSegmentInput }
  >
) => Promise<FetchResult<{ updateSegment: string }>>;

// Type for the estimate lazy query function passed from the component
type FetchEstimateQueryFn = LazyQueryExecFunction<
  { estimateSegmentDefinition: number },
  { input: { merchantId: string; segmentDefinition: SegmentDefinitionInput } }
>;

// --- Store Actions Interface ---

/** Defines the actions available to interact with and modify the segment builder store state. */
interface SegmentBuilderActions {
  /**
   * Initializes the store with data for an existing segment.
   * Resets dirty status and captures the initial state snapshot.
   * Assigns clientIds to groups and conditions if they are missing.
   * @param data - The initial segment data (name, description, operator, groups).
   */
  initialize: (data: {
    name: string;
    description: string;
    rootOperator: SegmentOperatorEnum;
    groups: ConditionGroupState[]; // Expect fully formed groups
  }) => void;
  /** Resets the store back to its default initial empty state. */
  reset: () => void;

  /** Updates the segment name. */
  setName: (name: string) => void;
  /** Updates the segment description. */
  setDescription: (description: string) => void;
  /** Sets the root operator combining the groups. */
  setRootOperator: (operator: SegmentOperatorEnum) => void;
  /**
   * Directly sets the groups array. Ensures clientIds are assigned.
   * Use with caution, prefer granular actions like addGroup/removeGroup.
   */
  setGroups: (groups: ConditionGroupState[]) => void; // Expect fully formed groups

  /** Adds a new, empty condition group to the segment definition. */
  addGroup: () => void;
  /** Removes a condition group by its client ID. */
  removeGroup: (groupClientId: string) => void;
  /** Sets the logical operator for a specific condition group. */
  setGroupOperator: (groupClientId: string, operator: SegmentOperatorEnum) => void;

  /** Adds a new condition to a specified condition group. */
  addConditionToGroup: (groupClientId: string, condition: AddConditionInput) => void;
  /** Removes a condition from a group by its client ID. */
  removeConditionFromGroup: (groupClientId: string, conditionClientId: string) => void;
  /** Updates an existing condition within a group. */
  updateConditionInGroup: (
    groupClientId: string,
    conditionClientId: string,
    updatedCondition: UpdateConditionInput
  ) => void;

  /**
   * Saves the current segment definition to the backend.
   * Requires the segment ID and the Apollo mutation function to be passed in.
   * Sets `isSaving` and `saveError` state during the operation.
   * Updates `isDirty` and `initialState` on success.
   * @param segmentId - The ID of the segment to update.
   * @param updateMutation - The Apollo mutation function (from useMutation).
   * @returns {Promise<boolean>} True if the save was successful, false otherwise.
   */
  saveSegment: (segmentId: string, updateMutation: UpdateSegmentMutationFn) => Promise<boolean>; // Accepts mutation fn
  /**
   * Fetches the estimated audience size for the current segment definition.
   * Requires the merchant ID and the Apollo lazy query function.
   * Sets `isEstimating`, `estimateCount`, and `estimateError` state.
   * @param merchantId - The ID of the current merchant.
   * @param estimateQuery - The Apollo lazy query execution function (from useLazyQuery).
   */
  fetchEstimate: (merchantId: string, estimateQuery: FetchEstimateQueryFn) => Promise<void>;

  /** Internal action (called by other actions) to recalculate the `isDirty` state. */
  _checkDirty: () => void;
  /** Internal helper to create a deep snapshot of the current editable state for dirty checking. */
  _createStateSnapshot: () => InitialStateSnapshot; // Helper for snapshotting
}

// --- Initial State ---
// Ensure initial state conforms to the interfaces
const initialState: SegmentBuilderState = {
  name: '',
  description: '',
  rootOperator: SegmentOperatorEnum.And,
  groups: [], // Initial groups array is empty
  isInitialized: false,
  isDirty: false,
  initialState: null,
  isSaving: false,
  saveError: null,
  isFetchingInitial: false,
  fetchInitialError: null,
  estimateCount: null,
  isEstimating: false,
  estimateError: null
};

// Type for the initial state snapshot stored for dirty checking
type InitialStateSnapshot = NonNullable<SegmentBuilderState['initialState']>;

// --- NEW Consolidated Mapping Helpers ---

/**
 * Maps a single ConditionState (from store) to the API structure.
 * Omits clientId and ensures timeBoundary is handled.
 */
function _mapStoreConditionToApiCondition(conditionState: ConditionState): Record<string, any> {
  const { clientId, ...apiCondition } = conditionState;
  return {
    ...apiCondition,
    timeBoundary: apiCondition.timeBoundary ?? undefined
  };
}

/**
 * Maps a single ConditionGroupState (from store) to ConditionGroupInput (for API).
 * Omits clientId and uses _mapStoreConditionToApiCondition for nested conditions.
 */
function _mapStoreGroupToApiGroup(groupState: ConditionGroupState): ConditionGroupInput {
  const { clientId, conditions, ...apiGroup } = groupState;
  return {
    ...apiGroup,
    conditions: conditions.map(_mapStoreConditionToApiCondition)
  };
}

/**
 * Consolidated function to map store state to the SegmentDefinitionInput structure.
 */
function _mapStoreStateToSegmentDefinitionInput(
  name: string,
  description: string,
  rootOperator: SegmentOperatorEnum,
  groups: ConditionGroupState[]
): SegmentDefinitionInput {
  return {
    name,
    description: description || undefined,
    rootOperator,
    groups: groups.map(_mapStoreGroupToApiGroup)
  };
}

// --- Store Implementation ---

export const useSegmentBuilderStore = create<SegmentBuilderState & SegmentBuilderActions>()(
  immer((set, get) => ({
    ...initialState,

    // --- Action Implementations ---

    initialize: (data) => {
      // Create snapshot directly from the input data
      const initialSnapshot = JSON.parse(
        JSON.stringify({
          name: data.name,
          description: data.description,
          rootOperator: data.rootOperator,
          groups: data.groups // Use groups directly from input
        })
      ) as InitialStateSnapshot;

      set((state) => {
        state.name = data.name;
        state.description = data.description;
        state.rootOperator = data.rootOperator;
        state.groups = data.groups; // Assign groups directly
        state.initialState = initialSnapshot;
        state.isInitialized = true;
        state.isDirty = false;
        state.isFetchingInitial = false;
        state.fetchInitialError = null;
      });
    },

    reset: () => {
      set(initialState);
    },

    setName: (name) => {
      set((state) => {
        state.name = name;
      });
      get()._checkDirty();
    },

    setDescription: (description) => {
      set((state) => {
        state.description = description;
      });
      get()._checkDirty();
    },

    setRootOperator: (operator) => {
      set((state) => {
        state.rootOperator = operator;
      });
      get()._checkDirty();
    },

    setGroups: (groups) => {
      // Keep processing here for direct setGroups calls, unless we enforce
      // pre-processing for this action too.
      const processedGroups = groups.map((g) => ({
        id: g.id,
        clientId: g.clientId || crypto.randomUUID(),
        operator: g.operator,
        conditions: g.conditions.map((c) => ({
          ...c,
          clientId: c.clientId || crypto.randomUUID(),
          conditionType: ConditionTypeEnum.Event as ConditionTypeEnum.Event,
          eventName: c.eventName,
          operator: c.operator,
          numTimes: c.numTimes,
          timeBoundary: c.timeBoundary ?? { type: TimeBoundaryType.AnyTime } // Keep default here for direct set
        }))
      }));
      set((state) => {
        state.groups = processedGroups;
      });
      get()._checkDirty();
    },

    addGroup: () => {
      set((state) => {
        state.groups.push({
          clientId: crypto.randomUUID(),
          operator: SegmentOperatorEnum.And,
          conditions: []
          // id is initially undefined
        });
      });
      get()._checkDirty();
    },

    removeGroup: (groupClientId) => {
      set((state) => {
        // Immer: Find index and splice
        const index = state.groups.findIndex((g) => g.clientId === groupClientId);
        if (index > -1) {
          state.groups.splice(index, 1);
        }
      });
      get()._checkDirty();
    },

    setGroupOperator: (groupClientId, operator) => {
      set((state) => {
        // Immer: Find group and mutate directly
        const group = state.groups.find((g) => g.clientId === groupClientId);
        if (group) {
          group.operator = operator;
        }
      });
      get()._checkDirty();
    },

    addConditionToGroup: (groupClientId, conditionInput) => {
      set((state) => {
        const group = state.groups.find((g) => g.clientId === groupClientId);
        if (group) {
          // Create the full ConditionState object
          const newCondition: ConditionState = {
            clientId: crypto.randomUUID(),
            conditionType: ConditionTypeEnum.Event,
            // Get required fields from input
            eventName: conditionInput.eventName,
            operator: conditionInput.operator,
            numTimes: conditionInput.numTimes,
            // Handle optional timeBoundary with default
            // Note: If API ensures timeBoundary, this default might only be needed
            // if conditions can be added without a timeBoundary initially.
            timeBoundary: conditionInput.timeBoundary ?? DEFAULT_TIME_BOUNDARY // Use constant
          };
          group.conditions.push(newCondition);
        }
      });
      get()._checkDirty();
    },

    removeConditionFromGroup: (groupClientId, conditionClientId) => {
      set((state) => {
        // Immer: Find group, then find condition index and splice
        const group = state.groups.find((g) => g.clientId === groupClientId);
        if (group) {
          const conditionIndex = group.conditions.findIndex(
            (c) => c.clientId === conditionClientId
          );
          if (conditionIndex > -1) {
            group.conditions.splice(conditionIndex, 1);
          }
        }
      });
      get()._checkDirty();
    },

    updateConditionInGroup: (groupClientId, conditionClientId, updatedConditionInput) => {
      set((state) => {
        // Immer: Find group and condition, then mutate properties directly
        const group = state.groups.find((g) => g.clientId === groupClientId);
        if (group) {
          const condition = group.conditions.find((c) => c.clientId === conditionClientId);
          if (condition) {
            // Directly modify properties if they exist in the input
            if (updatedConditionInput.eventName !== undefined) {
              condition.eventName = updatedConditionInput.eventName;
            }
            if (updatedConditionInput.operator !== undefined) {
              condition.operator = updatedConditionInput.operator;
            }
            if (updatedConditionInput.numTimes !== undefined) {
              condition.numTimes = updatedConditionInput.numTimes;
            }
            if (updatedConditionInput.timeBoundary !== undefined) {
              // Use constant for null case
              condition.timeBoundary = updatedConditionInput.timeBoundary ?? DEFAULT_TIME_BOUNDARY;
            }
          }
        }
      });
      get()._checkDirty();
    },

    _checkDirty: () => {
      set((state) => {
        if (!state.initialState) {
          state.isDirty = false;
          return;
        }
        const currentStateSnapshot = get()._createStateSnapshot();
        try {
          const currentString = JSON.stringify(currentStateSnapshot);
          const initialString = JSON.stringify(state.initialState);
          state.isDirty = currentString !== initialString;
        } catch (e) {
          console.error('Error comparing state for dirty check:', e);
          state.isDirty = true; // Assume dirty if comparison fails
        }
      });
    },

    // --- Snapshotting Helper ---
    _createStateSnapshot: () => {
      const state = get();
      const snapshot: InitialStateSnapshot = JSON.parse(
        JSON.stringify({
          name: state.name,
          description: state.description,
          rootOperator: state.rootOperator,
          groups: state.groups.map((g) => ({
            id: g.id,
            clientId: g.clientId,
            operator: g.operator,
            conditions: g.conditions.map((c) => ({
              clientId: c.clientId,
              conditionType: c.conditionType,
              eventName: c.eventName,
              operator: c.operator,
              numTimes: c.numTimes,
              timeBoundary: c.timeBoundary
            }))
          }))
        })
      ) as InitialStateSnapshot;
      return snapshot;
    },

    // --- Async Actions Implementation ---
    saveSegment: async (
      segmentId: string,
      updateMutation: UpdateSegmentMutationFn
    ): Promise<boolean> => {
      if (!segmentId) {
        console.error('Save Error: segmentId is required');
        set({ isSaving: false, saveError: 'Segment ID is missing' });
        return false;
      }

      set({ isSaving: true, saveError: null });
      const { name, description, rootOperator, groups } = get();

      try {
        // Use the consolidated function to get the core definition
        const segmentDefinition = _mapStoreStateToSegmentDefinitionInput(
          name,
          description,
          rootOperator,
          groups
        );

        // Construct the final CreateSegmentInput (assuming it just wraps the definition)
        // If CreateSegmentInput has other top-level fields, adjust here.
        const mutationInput: CreateSegmentInput = {
          ...segmentDefinition // Spread the fields from the definition
        };

        // Basic input validation (can check segmentDefinition.groups)
        if (segmentDefinition.groups.length === 0 && groups.length > 0) {
          throw new Error('Input mapping failed or produced empty groups');
        }

        // Perform the mutation using the passed function
        const result: FetchResult<{ updateSegment: string }> = await updateMutation({
          // Add type annotation
          variables: { segment_id: segmentId, input: mutationInput }
        });

        // Check for GraphQL errors in the result
        if (result.errors) {
          // Concatenate multiple error messages if they exist
          const errorMessage = result.errors
            .map((e: { message: string }) => e.message) // Add type for 'e'
            .join('\n');
          throw new Error(errorMessage);
        }

        // Check if data is missing (might indicate a problem even without errors)
        if (!result.data?.updateSegment) {
          throw new Error('Mutation completed but returned no data.');
        }

        // --- Success ---
        const newInitialState = get()._createStateSnapshot(); // Snapshot the *current* state as the new baseline
        set({
          isSaving: false,
          isDirty: false, // No longer dirty after successful save
          initialState: newInitialState, // Update baseline
          saveError: null
        });
        console.log('Segment saved successfully');
        return true; // Indicate success
      } catch (err: unknown) {
        // --- Error ---
        console.error('Failed to update segment:', err);
        // Type guard for error message extraction
        const message =
          err instanceof Error ? err.message : 'An unknown error occurred during save.';
        set({ isSaving: false, saveError: message });
        return false; // Indicate failure
      }
    },

    fetchEstimate: async (
      merchantId: string,
      estimateQuery: FetchEstimateQueryFn
    ): Promise<void> => {
      if (!merchantId) {
        console.error('Estimate Error: merchantId is required');
        set({ isEstimating: false, estimateError: 'Merchant ID is missing' });
        return;
      }

      set({ isEstimating: true, estimateError: null });
      const { name, description, rootOperator, groups } = get();

      try {
        // Use the consolidated function
        const segmentDefinitionInput = _mapStoreStateToSegmentDefinitionInput(
          name,
          description,
          rootOperator,
          groups
        );

        // Construct the variables object matching the new structure defined in FetchEstimateQueryFn
        const variables = {
          input: {
            merchantId: merchantId,
            segmentDefinition: segmentDefinitionInput
          }
        };

        const result = await estimateQuery({ variables });

        if (result.error) {
          throw result.error; // Throw ApolloError directly if present
        }

        // Extract the estimate count, handle potential null/undefined
        const estimate = result.data?.estimateSegmentDefinition;
        if (estimate == null) {
          console.warn('Estimate query succeeded but returned null/undefined count.');
        }

        // --- Success ---
        set({ estimateCount: estimate ?? null, isEstimating: false, estimateError: null });
      } catch (err: unknown) {
        // --- Error ---
        console.error('Failed to fetch estimate:', err);
        const message =
          err instanceof Error ? err.message : 'An unknown error occurred fetching estimate.';
        set({ isEstimating: false, estimateError: message, estimateCount: null });
      }
    }
  }))
);
